import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { HomesActions } from '@library/store/homes/homes.action';
import { HomesFacade } from '@library/store/homes/homes.facade';
import { PlaceUpdate } from '@library/store/homes/homes.interface';
import { UserFacade } from '@library/store/user/user.facade';
import { UserState } from '@library/store/user/user.state';
import { AtomsModule } from '@library/ui/atoms/atoms.module';
import { LayoutModule } from '@library/ui/layout/layout.module';
import { MoleculesModule } from '@library/ui/molecules/molecules.module';
import { Loader } from '@library/utils/classes/loader';
import { Const } from '@library/utils/constants/Const.constant';
import { BackOnAction } from '@library/utils/decorators/back-on-action.decorator';
import { isSubmitable } from '@library/utils/helpers/forms/is-sub';
import { isDefined } from '@library/utils/helpers/store/is-defined';
import { PlaceInfos } from '@library/utils/interfaces/place-infos.interface';
import { PipesModule } from '@library/utils/pipes/pipes.module';
import { JawgMapsService } from '@library/utils/services/jawgmaps.service';
import { UnitConverterService } from '@library/utils/services/unit-converter.service';
import { Actions } from '@ngrx/effects';
import type { LngLat } from 'maplibre-gl';
import { combineLatest, firstValueFrom, Observable, of } from 'rxjs';
import { debounceTime, map, switchMap } from 'rxjs/operators';
import { LocationMapComponent } from './location-map/location-map.component';

interface LocationForm {
  address: FormControl<string>;
}

@Component({
  selector: 'view-edit-user-location',
  templateUrl: './edit-user-location.component.html',
  styleUrls: ['./edit-user-location.component.scss'],
  imports: [
    CommonModule,
    PipesModule,
    FormsModule,
    ReactiveFormsModule,
    AtomsModule,
    LayoutModule,
    MoleculesModule,
    LocationMapComponent,
  ],
})
export class EditUserLocationComponent implements OnInit {
  private readonly actions = inject(Actions);
  private readonly homesFacade = inject(HomesFacade);
  private readonly userFacade = inject(UserFacade);
  private readonly jawgMapsService = inject(JawgMapsService);
  private readonly unitConverter = inject(UnitConverterService);

  destroyRef = inject(DestroyRef);

  selectedCoordinates = signal<LngLat>({ lat: 0, lng: 0 } as LngLat);
  selectedCoordinates$ = toObservable(this.selectedCoordinates);

  locationForm = new FormGroup<LocationForm>({
    address: new FormControl('', Validators.required),
  });

  isFormSubmittable$: Observable<boolean>;
  confirmLoader = new Loader(this.actions);

  viewModel$ = combineLatest([
    this.homesFacade.currentHomeId$,
    this.homesFacade.homeAltitude$,
    this.userFacade.currentUser$,
    this.selectedCoordinates$,
  ]).pipe(
    map(([homeId, homeAltitude, user, selectedCoordinates]) => {
      return {
        homeId,
        homeAltitude: this.formatAltitude(homeAltitude, user),
        selectedCoordinates,
      };
    }),
  );

  suggestedPlaces = signal<PlaceInfos[] | null>(null);

  async ngOnInit() {
    await this.jawgMapsService.loadMapLibre();
    const currentHome = await firstValueFrom(this.homesFacade.currentHome$.pipe(isDefined()));
    const currentCoordinates = new this.jawgMapsService.maplibre.LngLat(currentHome.place.coordinates[0], currentHome.place.coordinates[1]);
    this.selectedCoordinates.set(currentCoordinates);
    // Compare old and new coordinates to handle confirm button display
    this.isFormSubmittable$ = this.selectedCoordinates$.pipe(
      isSubmitable(of(currentCoordinates), this.locationForm),
    );

    this.locationForm.controls.address.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      debounceTime(300),
      switchMap((value) => (!!value ? this.jawgMapsService.getPlacesByText(value) : of(null))),
    ).subscribe((places) => { this.suggestedPlaces.set(places); });
  }

  private formatAltitude(homeAltitude: number, user: UserState): string {
    return user.unit_system === Const.NAPublicConst.UNIT_US
      ? `${this.unitConverter.convertMetersToFeet(homeAltitude)} ft`
      : `${homeAltitude} m`;
  }

  // set the new coordinates and update the address name in the search bar
  async updatePosition(coordinates: LngLat): Promise<void> {
    // Set the new coordinates
    this.selectedCoordinates.set(coordinates);
    const address = await firstValueFrom(this.jawgMapsService.getAddressByCoordinates(coordinates));
    this.locationForm.patchValue({ address }, { emitEvent: false });
  }

  closeDropdown() {
    this.suggestedPlaces.set(null);
  }

  @BackOnAction(
    HomesActions.updateHomePlaceSuccess.type,
    HomesActions.updateHomePlaceFailure.type,
  )
  updateHomeLocation(homeId: string, coordinates: LngLat): void {
    const placeUpdate: PlaceUpdate = {
      home_id: homeId,
      lat: coordinates.lat,
      lon: coordinates.lng,
      safe: true,
    };

    this.homesFacade.updatePlace(placeUpdate);
    this.confirmLoader.loadUntil([HomesActions.updateHomePlaceSuccess.type, HomesActions.updateHomePlaceFailure.type]);
    this.closeDropdown();
  }
}
